import { CircleNotch, Eye, EyeSlash } from "@phosphor-icons/react";
import * as React from "react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface PasswordInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * Optional loader for the *stored* secret. When provided and the field is
   * empty (nothing being typed), clicking the eye fetches the saved value and
   * shows it — for double-checking a secret that was already saved.
   * Without it, the eye just un-masks whatever is currently typed.
   */
  revealFetcher?: () => Promise<string | null | undefined>;
  /**
   * Hands the fetched secret back so the caller can hold it as its own draft.
   *
   * Without this the revealed secret is shown read-only, and a read-only field
   * is one you cannot select over and paste into. Replacing a saved hub token
   * was impossible for that reason, which left a beta house stuck behind a
   * token that was wrong and could not be corrected.
   */
  onReveal?: (value: string) => void;
}

/**
 * Password/secret input with a reveal toggle. The eye either un-masks what
 * you're typing, or — when the field is empty and a `revealFetcher` is given —
 * fetches and shows the already-saved secret read-only. Purely presentational;
 * value/onChange stay owned by the caller. `onMouseDown` is suppressed on the
 * toggle so clicking it doesn't blur (and prematurely commit) the field.
 */
const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, revealFetcher, onReveal, value, ...props }, ref) => {
    const [show, setShow] = React.useState(false);
    const [stored, setStored] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(false);

    const hasDraft = value != null && value !== "";
    const showingStored = show && !hasDraft && stored != null;

    const toggle = async () => {
      const next = !show;
      setShow(next);
      if (!next) {
        setStored(null);
        return;
      }
      if (!hasDraft && revealFetcher && stored == null) {
        setLoading(true);
        try {
          const secret = (await revealFetcher()) ?? null;
          // Handed to the caller when it wants it, so what you see is what you
          // can edit. Only fall back to showing it read-only here.
          if (secret != null && onReveal) {
            onReveal(secret);
          } else {
            setStored(secret);
          }
        } catch {
          setStored(null);
        } finally {
          setLoading(false);
        }
      }
    };

    return (
      <div className="relative flex-1">
        {showingStored ? (
          <Input readOnly type="text" value={stored ?? ""} className={cn("pr-9", className)} />
        ) : (
          <Input
            ref={ref}
            type={show ? "text" : "password"}
            value={value}
            className={cn("pr-9", className)}
            {...props}
          />
        )}
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => void toggle()}
          aria-label={show ? "Hide value" : "Reveal value"}
          aria-pressed={show}
          tabIndex={-1}
          className="text-muted hover:text-primary absolute top-1/2 right-2 -translate-y-1/2 rounded p-0.5 transition-colors"
        >
          {loading ? (
            <CircleNotch weight="light" className="text-muted size-4 animate-spin" />
          ) : show ? (
            <EyeSlash weight="light" className="text-accent-pressed size-4" />
          ) : (
            <Eye weight="light" className="size-4" />
          )}
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = "PasswordInput";

export { PasswordInput };
